import React, { useEffect, useState } from "react";
import { Flex, Form, Modal, notification, Spin } from "antd";
import {
  StyledForgotPasswordButton,
  StyledFrgotPassword,
  StyledLoginButton,
  StyledLoginTitle,
  StyledLoginWarningText,
  StyledLoginWrapper,
  StyledSentSuccessfully,
} from "./style";
import { ReactComponent as HiIcon } from "../../assets/icons/HandWithFingersSplayed.svg";
import { useAppSelector } from "../../store/config";
import { useAppDispatch } from "../../store/config";
import {
  forgotPasswordStart,
  loginRequest,
  resetForgotPassword,
} from "../../store/features/auth/auth-slice";
import { useTranslation } from "react-i18next";
import { TextInputField } from "../../components/inputs";
import { StyledForm } from "../../components/common/styles";

const LoginForm = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [api, contextHolder] = notification.useNotification();

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>("");

  const { loading, error } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (error && !modalOpen) {
      if (error.status === 4000) {
        setErrorText(t(error.message));
      } else {
        setErrorText("");
        openNotification();
      }
    } else {
      setErrorText("");
    }
  }, [error]);

  const onFinish = (values: any) => {
    dispatch(loginRequest(values));
  };

  const openNotification = () => {
    api["error"]({
      message: t("Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  return (
    <StyledLoginWrapper>
      <Spin spinning={loading && !modalOpen}>
        {contextHolder}
        <StyledLoginTitle level={2}>
          {t("Hi")} <HiIcon />
        </StyledLoginTitle>
        <StyledForm
          onFinish={onFinish}
          autoComplete="off"
          requiredMark={false}
          validateTrigger="onSubmit"
        >
          <Flex vertical gap={20}>
            <TextInputField
              label={t("Username")}
              isEditable={true}
              name="email"
              rules={[
                { required: true, message: t("Required") },
                {
                  type: "email",
                  message: t("Invalid email address"),
                },
              ]}
            />

            <Flex vertical>
              <TextInputField
                label={t("Password")}
                type="password"
                name="password"
                isEditable={true}
                rules={[
                  { required: true, message: t("Required") },
                  {
                    min: 6,
                    message: t("Password must be at least 6 characters"),
                  },
                  {
                    pattern: /(?=.*[a-z])/,
                    message: t(
                      "Password must contain at least one lowercase letter"
                    ),
                  },
                  {
                    pattern: /(?=.*[A-Z])/,
                    message: t(
                      "Password must contain at least one uppercase letter"
                    ),
                  },
                  {
                    pattern: /(?=.*\d)/,
                    message: t("Password must contain at least one number"),
                  },
                ]}
              />

              <StyledFrgotPassword onClick={() => setModalOpen(true)}>
                {t("Forgot Password?")}
              </StyledFrgotPassword>
            </Flex>
          </Flex>
          {errorText && (
            <StyledLoginWarningText>{t(errorText)}</StyledLoginWarningText>
          )}
          <StyledLoginButton type="text" htmlType="submit">
            {t("Login")}
          </StyledLoginButton>
        </StyledForm>
        <ForgotPasswordModal
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          openNotification={openNotification}
        />
      </Spin>
    </StyledLoginWrapper>
  );
};

export default LoginForm;

const ForgotPasswordModal = (props: {
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  openNotification: () => void;
}) => {
  const { modalOpen, setModalOpen, openNotification } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [errorText, setErrorText] = useState<string>("");
  const [sentSuccessfully, setSentSuccessfully] = useState<boolean>(false);

  const { loading, error, forgotPassword, requestState } = useAppSelector(
    (state) => state.auth
  );

  useEffect(() => {
    if (requestState === "finish" && forgotPassword) {
      setSentSuccessfully(true);
    }
  }, [forgotPassword, requestState]);

  useEffect(() => {
    if (error && modalOpen) {
      error.status === 3000
        ? setErrorText(t(error.message))
        : openNotification();
    } else {
      setErrorText("");
    }
  }, [error]);

  useEffect(() => {
    if (!modalOpen) {
      form.resetFields();
      setErrorText("");
      setSentSuccessfully(false);
    } else if (modalOpen) {
      dispatch(resetForgotPassword());
    }
  }, [modalOpen]);

  const sendForgotPassword = async (values: any) => {
    dispatch(forgotPasswordStart({ email: values.email }));
  };

  return (
    <Modal
      title={sentSuccessfully ? t("Sent Successfully") : t("Forgot Password?")}
      open={modalOpen}
      onCancel={() => setModalOpen(false)}
      footer={null}
      width={400}
      centered
      style={{ direction: "rtl" }}
    >
      <Spin spinning={loading}>
        <StyledForm
          form={form}
          onFinish={sendForgotPassword}
          validateTrigger="onSubmit"
        >
          {!sentSuccessfully && (
            <TextInputField
              label={t("Email Address")}
              isEditable={true}
              name="email"
              rules={[
                { required: true, message: t("Required") },
                {
                  type: "email",
                  message: t("Invalid email address"),
                },
              ]}
            />
          )}

          {sentSuccessfully ? (
            <>
              <StyledSentSuccessfully>
                {t("Password reset email sent successfully")}
              </StyledSentSuccessfully>
              <StyledForgotPasswordButton onClick={() => setModalOpen(false)}>
                {t("Close")}
              </StyledForgotPasswordButton>
            </>
          ) : (
            <>
              {errorText && modalOpen && (
                <StyledLoginWarningText>
                  {t("Account Not Found")}
                </StyledLoginWarningText>
              )}
              <StyledForgotPasswordButton htmlType="submit">
                {t("Send")}
              </StyledForgotPasswordButton>
            </>
          )}
        </StyledForm>
      </Spin>
    </Modal>
  );
};
